import { ClassScheduleSettings, type PageSectionLayout } from '../services/sectionLayoutService';
import { AcademicTerm, AnnualTimetableCell, Track } from '../types/domain';
import { canWriteSchoolScopedDataCache } from './schoolDataCacheVersion';
import { deviceCacheStorage } from './deviceCacheStorage';

const PROGRAM_PROGRESS_DATA_CACHE_VERSION = 'v4';

export type ProgramProgressDataCacheState = {
  terms: AcademicTerm[];
  schedule: ClassScheduleSettings | null;
  tracks: Track[];
  gradeTrackSections: PageSectionLayout[];
  regularColors: string[];
  dedicatedColors: string[];
  baseExcludeColors: string[];
  holidayDays: string[];
  cellsByKey: Record<string, AnnualTimetableCell>;
};

type ProgramProgressDataCachePayload = ProgramProgressDataCacheState & {
  savedAt: number;
};

function storageKey(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): string {
  return [
    'cp.programProgressData',
    PROGRAM_PROGRESS_DATA_CACHE_VERSION,
    workspaceId,
    academicYear,
    termNumber,
    gradeId,
  ].join('.');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function normalizeStringArray(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((value): value is string => typeof value === 'string');
}

function normalizeCellsByKey(raw: unknown): Record<string, AnnualTimetableCell> {
  if (!isRecord(raw)) return {};
  return Object.entries(raw).reduce<Record<string, AnnualTimetableCell>>((acc, [key, value]) => {
    if (value && typeof value === 'object') acc[key] = value as AnnualTimetableCell;
    return acc;
  }, {});
}

export function loadProgramProgressDataCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): { found: boolean; state: ProgramProgressDataCacheState } {
  const emptyState: ProgramProgressDataCacheState = {
    terms: [],
    schedule: null,
    tracks: [],
    gradeTrackSections: [],
    regularColors: [],
    dedicatedColors: [],
    baseExcludeColors: [],
    holidayDays: [],
    cellsByKey: {},
  };
  if (!workspaceId || !gradeId || typeof window === 'undefined') {
    return { found: false, state: emptyState };
  }
  try {
    const raw = deviceCacheStorage.getItem(storageKey(workspaceId, academicYear, termNumber, gradeId));
    if (!raw) return { found: false, state: emptyState };
    const parsed = JSON.parse(raw) as Partial<ProgramProgressDataCachePayload> | null;
    if (!parsed || typeof parsed !== 'object') return { found: false, state: emptyState };
    return {
      found: true,
      state: {
        terms: Array.isArray(parsed.terms) ? (parsed.terms as AcademicTerm[]) : [],
        schedule: (parsed.schedule as ClassScheduleSettings | null) ?? null,
        tracks: Array.isArray(parsed.tracks) ? (parsed.tracks as Track[]) : [],
        gradeTrackSections: Array.isArray(parsed.gradeTrackSections)
          ? (parsed.gradeTrackSections as PageSectionLayout[])
          : [],
        regularColors: normalizeStringArray(parsed.regularColors),
        dedicatedColors: normalizeStringArray(parsed.dedicatedColors),
        baseExcludeColors: normalizeStringArray(parsed.baseExcludeColors),
        holidayDays: normalizeStringArray(parsed.holidayDays),
        cellsByKey: normalizeCellsByKey(parsed.cellsByKey),
      },
    };
  } catch {
    return { found: false, state: emptyState };
  }
}

export function saveProgramProgressDataCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  state: ProgramProgressDataCacheState
): void {
  if (!workspaceId || !gradeId || typeof window === 'undefined' || !canWriteSchoolScopedDataCache(workspaceId)) return;
  try {
    const payload: ProgramProgressDataCachePayload = {
      ...state,
      savedAt: Date.now(),
    };
    deviceCacheStorage.setItem(
      storageKey(workspaceId, academicYear, termNumber, gradeId),
      JSON.stringify(payload)
    );
  } catch {
    // ignore quota / private mode write errors
  }
}

export function clearProgramProgressDataCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): void {
  if (!workspaceId || !gradeId || typeof window === 'undefined') return;
  try {
    deviceCacheStorage.removeItem(storageKey(workspaceId, academicYear, termNumber, gradeId));
  } catch {
    // ignore storage removal failures
  }
}

/** 프로그램 저장·연간 연동 시 스케줄·학기 메타는 유지하고 진도 셀(`cellsByKey`)만 비운다. */
export function clearProgramProgressVolatileInCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): void {
  const loaded = loadProgramProgressDataCache(workspaceId, academicYear, termNumber, gradeId);
  if (!loaded.found) return;
  saveProgramProgressDataCache(workspaceId, academicYear, termNumber, gradeId, {
    ...loaded.state,
    cellsByKey: {},
  });
}
